import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { type IPricingPlan } from "@/types/billing.types";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { PlanItem } from "../PlanItem/PlanItem";
import { VARIANT } from "@/constant";
import SmoothScrollStyle from "@/components/common/SmoothScrollStyle/SmoothScrollStyle";
import { MdTimer } from "react-icons/md";
import { GrClose } from "react-icons/gr";
import style from "@/components/page-modules/pricing/PricingPopup/PricingPopup.module.scss";
import { usePricingExitDiscountPopupState } from "../data/usePricingExitDiscountPopup.state";
import useApplyDiscount from "@/hooks/payment/useApplyDiscount";
import { DEBUGGER } from "@/helper/helper";
import { sendGTMEvent } from "@next/third-parties/google";
import CountdownTimer from "@/components/common/CountdownTimer";
import usePricing from "@/hooks/pages/usePricing";
import { gtm } from "@/gtm/events";
import PlanItemSkeleton from "../PlanItemSkeleton";
import IFThen from "@/components/common/IFThen";
const PricingPopup = () => {
  const t = useTranslations("Pricing");
  const [selectedPlan, setSelectedPlan] = useState<IPricingPlan | null>(null);
  const {
    setIsShow,
    setHasSeen
  } = usePricingExitDiscountPopupState();
  const {
    applyDiscount
  } = useApplyDiscount();
  const {
    data: pricingData,
    isLoading
  } = usePricing();
  const router = useRouter();
  useEffect(() => {
    sendGTMEvent({
      event: gtm.pricing.discount_popup_visible
    });
    const handleBeforeUnload = () => onClose(() => sendGTMEventOnClose());
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
  const handlePlanBtn = async (item: IPricingPlan, idx: number) => {
    const {
      uuid,
      name
    } = item;
    try {
      await applyDiscount();
      sendGTMEvent({
        event: `${idx + 1}_pricing_plan`,
        name,
        pathname: router.pathname
      });
      router.push({
        pathname: `/checkout`,
        query: {
          uuid
        }
      });
    } catch (e) {
      DEBUGGER(e);
    } finally {
      onClose();
    }
  };
  function onClose(cb?: () => void) {
    setIsShow(false);
    setHasSeen(true);
    cb && cb();
  }
  function sendGTMEventOnClose() {
    sendGTMEvent({
      event: gtm.pricing.discount_popup_close
    });
  }
  const data = pricingData?.data.map(item => {
    const discount = 30;
    const price = Number(item.amount.substring(1));
    const discountPrice = price * (1 - discount / 100);
    const discountAmountRounded = parseFloat(discountPrice.toFixed(2));
    const discountAmount = item.amount.substring(0, 1) + discountAmountRounded;
    return {
      ...item,
      discount: 30,
      discountAmount
    };
  });
  return <motion.div initial={{
    scale: 0.5,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} exit={{
    scale: 0.5,
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: "easeOut"
  }} className={style.pricingPopup} data-sentry-element="unknown" data-sentry-component="PricingPopup" data-sentry-source-file="index.tsx">
      <SmoothScrollStyle data-sentry-element="SmoothScrollStyle" data-sentry-source-file="index.tsx" />
      <div className={style.wrapper}>
        <GrClose onClick={() => onClose(() => sendGTMEventOnClose())} className={style.close} data-sentry-element="GrClose" data-sentry-source-file="index.tsx" />
        <div className={style.pricing_DashboardHeader}>
          <div className={style.timer}>
            <MdTimer size={20} color="#E45541" data-sentry-element="MdTimer" data-sentry-source-file="index.tsx" />
            <p>Offer expires in <span><CountdownTimer initialTimeLeft={300} cb={onClose} data-sentry-element="CountdownTimer" data-sentry-source-file="index.tsx" /></span></p>
          </div>
          <div className={style.container}>
            <h1 className={style.title}>
              Wait! Special Offer Just for You – <span className={style.discount}>30% Off!</span>
            </h1>
            <p className={style.des}>
              Before you go, grab this one-time discount to keep your QR codes active.
            </p>
          </div>
        </div>
        <div className={style.plans_box}>
          <div data-testid="plans" className={style.row}>

            <IFThen condition={isLoading} Then={Array.from({
            length: 3
          }).map((_, index) => <PlanItemSkeleton key={index} />)} Else={data?.map((item, index) => <PlanItem handleCheckout={() => handlePlanBtn(item, index)} key={item.uuid} index={index} selectedPlanName={selectedPlan?.name!} onSelectPlan={() => setSelectedPlan(item as any)} item={item as any} discount={item?.discount} TEST_VARIANT={VARIANT.VariantB} />)} data-sentry-element="IFThen" data-sentry-source-file="index.tsx" />

          </div>
          <p className={style.info_vat}>*{t("vatNote")}</p>
        </div>
      </div>
    </motion.div>;
};
export default PricingPopup;